import { ExperienceFeedbackDialog, HolobuilderIcon, IconButton, Text, Tooltip } from "@flat-ui";
import { useStellarApiClient } from "@hooks/use-stellar-api-client";
import { Box } from "@src/mui-wrapper";
import { runtimeConfig } from "@src/runtime-config";
import { devFeaturesStateSelector } from "@store/dev-features/dev-features-selector";
import { selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { withEllipsis } from "@styles/common-styles";
import { PALETTE_VARS } from "@styles/palette-vars";
import { isProductionEnvironment } from "@utils/env-utils";
import { sentryCaptureError } from "@utils/sentry-utils";
import { SidebarEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";
import { getHBDashboardUrl } from "@components/sidebar/sidebar-holobuilder/sidebar-holobuilder-utils";
import { EmailUtils } from "@stellar/utils";

/** Label for the button to switch back to the legacy UI */
const LABEL = "Switch back to legacy UI";
/** Renders the button to switch back to the legacy UI for HB->XG migrated workspaces */
export function SidebarSwitchToLegacyUI({
  isSidebarFullSize
}) {
  const {
    stellarApiClient
  } = useStellarApiClient();
  const selectedCompany = useAppSelector(selectedSdbCompanySelector);
  const currentUser = useAppSelector(currentUserSelector);
  const {
    isProductFeedbackEnabled
  } = useAppSelector(devFeaturesStateSelector);
  const {
    trackAsyncEvent
  } = useTrackEvent();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isInternalUser = EmailUtils.isInternalUser(currentUser?.email ?? "");

  // Only actual customers in production environment or enabling the dev feature to send feedback message
  // can actually send a message to the Teams channel
  const canSendMessage = !isInternalUser && isProductionEnvironment || isProductFeedbackEnabled;
  async function navigateToHBDashboard() {
    try {
      await trackAsyncEvent({
        name: SidebarEvents.navigateToHBDashboard
      });
    } catch {
      // Ignore errors from tracking the event
    }

    // Reset the dialog before leaving, so that pressing Back (restoring this page
    // from the back/forward cache) doesn't bring it back open or disabled
    setIsOpen(false);
    setIsSubmitting(false);
    const hbUrl = getHBDashboardUrl();
    window.location.assign(hbUrl);
  }
  async function onSubmitFeedback(reasons, feedbackText) {
    setIsSubmitting(true);

    // If the user cannot send a message, navigate to the HoloBuilder dashboard directly.
    if (!canSendMessage) {
      void navigateToHBDashboard();
      return;
    }
    try {
      await stellarApiClient.feedback.submitFeedback({
        application: "Sphere XG Dashboard",
        appVersion: runtimeConfig.appVersion,
        featureName: "Sphere XG redirect to HoloBuilder",
        reasons,
        feedbackMessage: feedbackText.trim(),
        channel: "product-feedback",
        companyId: selectedCompany?.id,
        userId: currentUser?.id,
        userEmail: currentUser?.email,
        // The Power automate flow for the webhook that sends messages to the Product Feedback channel
        // has a condition to only publish messages if isInternalUser is false:
        // https://make.powerautomate.com/environments/3c7a3962-5bba-e1b7-b359-2f0f9e8b8b08/solutions/~preferred/flows/c4bf6b51-f408-f111-8407-7c1e526a253a/details
        // Since we already did our own check with canSendMessage, we can safely set isInternalUser to false.
        isInternalUser: false
      });
    } catch (error) {
      sentryCaptureError({
        error,
        title: "Error calling feedback route for switching to legacy UI"
      });
    } finally {
      await navigateToHBDashboard();
    }
  }
  return <>
      <Tooltip title={LABEL}>
        <Box sx={{
        height: "60px",
        display: "flex",
        alignItems: "center"
      }}>
          {isSidebarFullSize ? <Text variant="bodyS" component="button" onClick={() => setIsOpen(true)} aria-label={LABEL} sx={{
          pl: "18px",
          border: "none",
          background: "none",
          color: PALETTE_VARS.text.secondary,
          textDecoration: "underline",
          cursor: "pointer",
          ...withEllipsis
        }}>
              {LABEL}
            </Text> : <IconButton onClick={() => setIsOpen(true)} aria-label={LABEL} color={PALETTE_VARS.text.secondary} sx={{
          ml: "10px"
        }}>
              <HolobuilderIcon />
            </IconButton>}
        </Box>
      </Tooltip>
      <ExperienceFeedbackDialog open={isOpen} onSubmitFeedback={onSubmitFeedback} onClose={() => setIsOpen(false)} onCancel={() => setIsOpen(false)} disabled={isSubmitting} />
    </>;
}